import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of, Subscriber } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { OAuthService } from 'angular-oauth2-oidc';
import { AssistantError, AssistantResponse, AssistantStreamEvent, ConversationTurn } from '@cumlaude/service-contract';
import { AuthService } from '@cumlaude/shared-authentication';
import { ENV_CONFIG, EnvConfiguration } from '@cumlaude/shared-configuration';
import { SKIP_SERVER_ERROR_HANDLING } from '../core/server-error-interceptor';
import { RolHeaderService } from './rol-header.service';

/**
 * Een mislukte streaming-aanroep van `/assistant/stream`. Draagt de foutbody van de backend mee als die
 * er was: het tokenverbruik van een mislukte aanroep zit daarin, en juist die gevallen zijn de
 * duurste. Bij een netwerk- of protocolfout is er geen body en blijft alleen `message` over.
 */
export class AssistantStreamError extends Error {
	constructor(
		message: string,
		readonly fout?: AssistantError
	) {
		super(message);
	}

	/**
	 * De data van een `error`-event. De backend stuurt daarin de foutbody als JSON; alles wat daar
	 * niet op lijkt (bijvoorbeeld een exception vóór het eerste event) wordt als losse melding
	 * behandeld.
	 */
	static fromEventData(data: string): AssistantStreamError {
		const tekst = data.trim();
		try {
			const fout = JSON.parse(tekst) as AssistantError;
			return new AssistantStreamError(fout.melding, fout);
		} catch {
			return new AssistantStreamError(tekst);
		}
	}
}

/**
 * Zet één SSE-event om in wat de subscriber ervan moet zien. Het `result`-event completeert de
 * subscriber: er komt er precies één, en daarna valt er niets meer te verwachten.
 */
function emitAssistantStreamEvent(raw: string, subscriber: Subscriber<AssistantStreamEvent>): void {
	let eventType = '';
	const dataLines: string[] = [];

	for (const line of raw.split('\n')) {
		if (line.startsWith('event:')) eventType = line.slice(6).trim();
		else if (line.startsWith('data:')) dataLines.push(line.slice(5));
	}

	const data = dataLines.join('\n');
	switch (eventType) {
		case 'progress':
			subscriber.next({ type: 'progress', message: data.trim() });
			break;
		case 'result':
			subscriber.next({ type: 'result', response: JSON.parse(data) });
			subscriber.complete();
			break;
		case 'error':
			subscriber.error(AssistantStreamError.fromEventData(data));
			break;
	}
}

/**
 * De aanroepen naar het `/assistant`-endpoint van de dataservice: beschikbaarheid opvragen en een vraag
 * aan het model stellen.
 *
 * Niet te verwarren met `AiChatService`, die alleen de chat-sidebar opent op basis van een
 * query-parameter. Deze service doet het transport, die de UI.
 */
@Injectable({
	providedIn: 'root',
})
export class AssistantService {
	private readonly http = inject(HttpClient);
	private readonly envConfig = inject<EnvConfiguration>(ENV_CONFIG);
	private readonly authService = inject(AuthService);
	private readonly oauthService = inject(OAuthService);
	private readonly rolHeaderService = inject(RolHeaderService);

	isAvailable() {
		const url = `${this.envConfig.dataUrl}/assistant/available`;
		return this.authService.refreshTokenIfNeeded().pipe(
			switchMap(() =>
				this.http.get<boolean>(url, {
					headers: this.rolHeaderService.getActiveRolHeader(),
					observe: 'body',
					responseType: 'json',
					context: new HttpContext().set(SKIP_SERVER_ERROR_HANDLING, true),
				})
			),
			catchError(() => of(false))
		);
	}

	/**
	 * De niet-streamende variant. Heeft op dit moment geen aanroepers — de chat gebruikt
	 * {@link answerStream} — maar het endpoint bestaat nog en geeft hetzelfde antwoord.
	 */
	answer(conversation: ConversationTurn[], geen_vo: boolean, geen_vso: boolean): Observable<AssistantResponse> {
		const url = `${this.envConfig.dataUrl}/assistant`;
		return this.authService.refreshTokenIfNeeded().pipe(
			switchMap(() =>
				this.http.post<AssistantResponse>(url, conversation, {
					params: { geen_vo, geen_vso },
					headers: { Accept: 'application/json', ...this.rolHeaderService.getActiveRolHeader() },
					observe: 'body',
					responseType: 'json',
				})
			)
		);
	}

	answerStream(conversation: ConversationTurn[], geen_vo: boolean, geen_vso: boolean): Observable<AssistantStreamEvent> {
		const url = new URL(`${this.envConfig.dataUrl}/assistant/stream`);
		url.searchParams.set('geen_vo', String(geen_vo));
		url.searchParams.set('geen_vso', String(geen_vso));

		return this.authService.refreshTokenIfNeeded().pipe(
			switchMap(
				() =>
					new Observable<AssistantStreamEvent>((subscriber) => {
						// De aanroep start hier en niet in answerStream() zelf, zodat er niets naar het model
						// gaat zonder dat iemand meeleest. De teardown breekt het request af: een verlaten
						// stream houdt de verbinding anders bezet tot het model klaar is, en dat is met een
						// tool-ronde erbij lang genoeg om in de weg te zitten.
						const controller = new AbortController();
						void this.readAnswerStream(url, conversation, controller.signal, subscriber);
						return () => controller.abort();
					})
			)
		);
	}

	/**
	 * Leest de SSE-stroom uit en zet hem om in events. Niet via HttpClient: die geeft de body pas terug
	 * als het antwoord compleet is, en dan valt er niets meer te tonen onderweg. Daarmee valt ook de
	 * interceptor-chain weg, dus de Authorization-header moet hier met de hand mee.
	 *
	 * Een afgebroken stream is geen fout — de subscriber is dan al weg en luistert niet meer.
	 */
	private async readAnswerStream(
		url: URL,
		conversation: ConversationTurn[],
		signal: AbortSignal,
		subscriber: Subscriber<AssistantStreamEvent>
	): Promise<void> {
		try {
			const response = await fetch(url.toString(), {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Accept: 'text/event-stream',
					Authorization: this.oauthService.authorizationHeader(),
					...(this.rolHeaderService.getActiveRolHeader() as Record<string, string>),
				},
				body: JSON.stringify(conversation),
				signal,
			});

			if (!response.ok || !response.body) throw new AssistantStreamError(`HTTP ${response.status}`);

			const reader = response.body.getReader();
			try {
				const decoder = new TextDecoder();
				let buffer = '';

				// closed dekt het result-event: dat completeert de subscriber, en dan hoeft de rest van
				// de stroom niet meer gelezen te worden.
				while (!subscriber.closed) {
					const { done, value } = await reader.read();
					if (done) break;

					buffer += decoder.decode(value, { stream: true });

					// SSE-events worden gescheiden door een lege regel (\n\n). Data binnen één event kan
					// over meerdere data:-regels verdeeld zijn, die eerst samengevoegd moeten worden.
					const events = buffer.split('\n\n');
					buffer = events.pop()!;
					for (const event of events) emitAssistantStreamEvent(event, subscriber);
				}
			} finally {
				// Geeft de verbinding vrij als we stoppen vóór het einde van de stroom.
				void reader.cancel().catch(() => undefined);
			}
			subscriber.complete();
		} catch (error) {
			if (signal.aborted) return;
			subscriber.error(error);
		}
	}
}
